<template>
  <div class="app-wrapper">
    <!-- 侧边栏 -->
    <div class="sidebar-container">
      <el-menu
        :default-active="$route.path"
        class="el-menu-vertical"
        :router="true"
        background-color="#304156"
        text-color="#bfcbd9"
        active-text-color="#409EFF"
      >
        <template v-for="route in menuRoutes" :key="route.path">
          <!-- 单级菜单 -->
          <el-menu-item v-if="!route.children || route.children.length === 1" :index="getMenuIndex(route)">
            <el-icon><component :is="getIcon(route.meta?.icon)" /></el-icon>
            <span>{{ route.meta?.title || route.name }}</span>
          </el-menu-item>
          
          <!-- 多级菜单 -->
          <el-sub-menu v-else :index="route.path">
            <template #title>
              <el-icon><component :is="getIcon(route.meta?.icon)" /></el-icon>
              <span>{{ route.meta?.title || route.name }}</span>
            </template>
            <el-menu-item 
              v-for="child in route.children" 
              :key="child.path" 
              :index="route.path + '/' + child.path"
            >
              {{ child.meta?.title || child.name }}
            </el-menu-item>
          </el-sub-menu>
        </template>
      </el-menu>
    </div>

    <!-- 主内容区 -->
    <div class="main-container">
      <!-- 顶部导航栏 -->
      <div class="navbar">
        <div class="hamburger-container">
          <el-icon><Menu /></el-icon>
        </div>
        <breadcrumb class="breadcrumb-container" />
        <div class="right-menu">
          <el-dropdown trigger="click">
            <div class="avatar-wrapper">
              <el-avatar :size="30" :src="userAvatar" />
              <span class="user-name">{{ userName }}</span>
              <el-icon><CaretBottom /></el-icon>
            </div>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item @click="handleProfile">个人信息</el-dropdown-item>
                <el-dropdown-item divided @click="handleLogout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
      
      <!-- 内容区 -->
      <div class="app-main">
        <router-view />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { ElMessageBox } from 'element-plus'
import { 
  Menu, CaretBottom, Monitor, User, Key, Document, Connection, Wallet,
  Dashboard, Cpu, Money
} from '@element-plus/icons-vue'
import Breadcrumb from '@/components/Breadcrumb.vue'
import { constantRoutes } from '@/router'

const store = useStore()
const router = useRouter()

// 用户信息
const userName = computed(() => store.state.user.userInfo.username || '管理员')
const userAvatar = computed(() => store.state.user.userInfo.avatar || 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png')

// 图标映射
const iconMap = {
  dashboard: Dashboard,
  user: User,
  key: Key,
  document: Document,
  cpu: Cpu,
  money: Money,
  connection: Connection,
  wallet: Wallet
}

// 从路由生成菜单
const menuRoutes = computed(() => {
  return constantRoutes.filter(route => {
    // 过滤掉隐藏的路由和登录页
    return !route.hidden && route.path !== '/login' && route.path !== '/:pathMatch(.*)*'
  })
})

// 获取图标组件
const getIcon = (iconName) => {
  return iconMap[iconName] || Monitor
}

// 获取菜单索引
const getMenuIndex = (route) => {
  if (route.children && route.children.length === 1) {
    return route.path + '/' + route.children[0].path
  }
  return route.path
}

// 处理个人信息
const handleProfile = () => {
  // 跳转到个人信息页面
  router.push('/profile')
}

// 处理退出登录
const handleLogout = () => {
  ElMessageBox.confirm('确定要退出登录吗?', '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(() => {
    store.dispatch('user/logout').then(() => {
      router.push('/login')
    })
  }).catch(() => {})
}
</script>

<style scoped>
.app-wrapper {
  display: flex;
  height: 100vh;
  width: 100%;
}

.sidebar-container {
  width: 210px;
  height: 100%;
  background-color: #304156;
  transition: width 0.28s;
  overflow-y: auto;
}

.main-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.navbar {
  height: 50px;
  display: flex;
  align-items: center;
  background-color: #fff;
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);
}

.hamburger-container {
  padding: 0 15px;
  cursor: pointer;
}

.breadcrumb-container {
  flex: 1;
  padding-left: 15px;
}

.right-menu {
  display: flex;
  align-items: center;
  padding-right: 20px;
}

.avatar-wrapper {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.user-name {
  margin: 0 5px;
}

.app-main {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  background-color: #f0f2f5;
}

.el-menu-vertical {
  border-right: none;
}
</style>